"use client";

import { Plus, X } from "lucide-react";
import { pluralize } from "@/lib/utils";
import { SimpleCombobox } from "@/components/SimpleCombobox";
import { US_STATE_OPTIONS } from "@/lib/constants/usStates";

interface SchoolSetupFormProps {
  displayName: string;
  schoolNames: string[];
  setSchoolNames: React.Dispatch<React.SetStateAction<string[]>>;
  selectedState: string;
  setSelectedState: (state: string) => void;
  schoolSetupError: string;
  schoolSetupSubmitting: boolean;
  onSubmit: () => void;
  hasReferral: boolean;
  referrerName: string | null;
}

export function SchoolSetupForm({
  displayName,
  schoolNames,
  setSchoolNames,
  selectedState,
  setSelectedState,
  schoolSetupError,
  schoolSetupSubmitting,
  onSubmit,
  hasReferral,
  referrerName,
}: SchoolSetupFormProps) {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="rounded-sm bg-white shadow-[0_2px_15px_rgba(0,0,0,0.08)] px-8 py-8 md:px-14 md:py-12 relative"
        style={{ backgroundImage: 'linear-gradient(to bottom, transparent 95%, #f5f0e8)' }}
      >
        {/* Decorative top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 via-orange-300 to-purple-400" />

        {/* Salutation */}
        <p
          className="text-4xl md:text-5xl text-gray-900 mb-4"
          style={{ fontFamily: "Alex Brush, cursive" }}
        >
          Hey {displayName ? displayName.split(' ')[0].charAt(0).toUpperCase() + displayName.split(' ')[0].slice(1) : ''}!
        </p>

        <h3
          className="text-lg md:text-xl font-bold text-gray-800 mb-5"
          style={{ fontFamily: 'Cormorant Garamond, serif', letterSpacing: '0.02em' }}
        >
          Welcome to The Hub
        </h3>

        <div className="space-y-4 text-[15px] text-gray-700 leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
          <p>
            I built The Hub because I know firsthand how overwhelming it is to run a childcare program. You probably already have tools for parent communication and daily tracking &mdash; and those are great for that. But what about everything behind the scenes? Compliance deadlines, staff onboarding, budgets, evaluations, documentation &mdash; the inner webs of childcare that actually keep your program running?
          </p>
          <p>
            That&apos;s exactly what The Hub is for. Think of it as your internal command center &mdash; a set of connected hubs, each built for a specific part of managing your program from the inside out. No parent-facing features, just the back-office tools directors actually need.
          </p>
          <p>
            Head to Settings to choose your hubs and subscribe &mdash; the more you bundle, the more you save.
          </p>
        </div>


        {/* Referral discount banner */}
        {hasReferral && (
          <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-center">
            <p className="text-sm font-medium text-emerald-800">
              {referrerName ? `${referrerName} referred you` : "You\u2019ve been referred"}! Get <span className="font-bold">20% off</span> your first month when you subscribe.
            </p>
          </div>
        )}

        {/* Signature */}
        <p className="mt-6 text-gray-600" style={{ fontFamily: 'Alex Brush, cursive', fontSize: '1.5rem' }}>
          Halle Melendez
        </p>
        <p className="text-xs text-gray-500 -mt-1" style={{ fontFamily: 'Georgia, serif' }}>
          Founder, Admin4You LLC
        </p>

        {/* School setup section */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <h4
            className="text-lg font-bold text-gray-800 mb-1"
            style={{ fontFamily: 'Cormorant Garamond, serif', letterSpacing: '0.02em' }}
          >
            Name Your {pluralize(schoolNames.length, 'School')}
          </h4>
          <p className="text-sm text-muted-foreground mb-4">
            Add each school or center you manage. You can always add more later in Settings.
          </p>

          <div className="space-y-3 mb-4">
            {schoolNames.map((value, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={value}
                  onChange={e => {
                    setSchoolNames(prev => prev.map((n, i) => i === index ? e.target.value : n));
                  }}
                  placeholder={index === 0 ? 'e.g., Sunshine Daycare' : 'e.g., Little Stars Academy'}
                  autoFocus={index === schoolNames.length - 1}
                  maxLength={100}
                  className="flex-1 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => setSchoolNames(prev => prev.filter((_, i) => i !== index))}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setSchoolNames(prev => [...prev, ''])}
            className="flex items-center gap-1.5 text-sm text-purple-600 hover:text-purple-700 transition-colors mb-5"
          >
            <Plus className="h-4 w-4" />
            Add another school
          </button>

          <div className="mb-5">
            {/* Was labelled "(optional)", which read as "skip me" — and most people did, leaving
                two thirds of centres with no state and no licensing rules. Still not enforced,
                but the reason to answer is now on the label instead of a disclaimer against it. */}
            <label className="block text-sm text-gray-600 mb-1.5">Which state are you in?</label>
            <div className="max-w-xs">
              <SimpleCombobox
                value={selectedState}
                onValueChange={setSelectedState}
                options={US_STATE_OPTIONS}
                placeholder="Select state…"
              />
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              So we can show your state&apos;s licensing rules and official requirements.
            </p>
          </div>

          {schoolSetupError && (
            <p className="text-sm text-destructive mb-4">{schoolSetupError}</p>
          )}

          <button
            onClick={onSubmit}
            disabled={!schoolNames[0].trim() || schoolSetupSubmitting}
            className="w-full sm:w-auto px-8 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-purple-600 to-orange-500 hover:shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {schoolSetupSubmitting ? 'Setting up...' : 'Get Started'}
          </button>
        </div>
      </div>
    </div>
  );
}
